import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import browserHistory from '../../lib/history.js';
import ProductListRow from '../products/item-list-row.js';
import MemoizedProductListRow from '../products/memoized-product-list-row.js';
import ProductListRowLoading from '../products/item-list-row-loading.js';
import {
  doGetPopularCategoryProducts,
  doGetCategoryProducts,
} from '../../actions/product-category-actions.js';
import { SEE_ALL_CATEGORY_THRESHOLD } from '../../strings.js';
import {
  filterInLimitedCatalog,
  mergePopularProducts,
} from '../../helpers/product-helpers.js';
import { mapLocationParentCategoriesIds } from '../../helpers/product-category-helpers.js';
import './officeluv-catalog.css';

export class OfficeluvFullCatalog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loadingProgress: new Set(),
      requestCache: new Map(),
    };

    this.loadProductsProgressively(props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isRequesting) return;

    this.loadProductsProgressively(nextProps);
  }

  getCacheKey(categoryId, type) {
    return `${categoryId}-${type}`;
  }

  isRequestInFlight(categoryId, type) {
    const cacheKey = this.getCacheKey(categoryId, type);
    return this.state.requestCache.has(cacheKey);
  }

  markRequestStarted(categoryId, type) {
    const cacheKey = this.getCacheKey(categoryId, type);
    this.setState(prevState => ({
      requestCache: new Map(prevState.requestCache.set(cacheKey, true))
    }));
  }

  markRequestCompleted(categoryId, type) {
    const cacheKey = this.getCacheKey(categoryId, type);
    this.setState(prevState => {
      const newCache = new Map(prevState.requestCache);
      newCache.delete(cacheKey);
      return { requestCache: newCache };
    });
  }

  loadProductsProgressively(props) {
    // Phase 1: Load popular products immediately for visible categories
    const visibleCategories = props.categories.slice(0, 6); // Load first 6 categories immediately
    visibleCategories.forEach((category, index) => {
      if (!category.popular_products && !this.isRequestInFlight(category.id, 'popular')) {
        this.markRequestStarted(category.id, 'popular');
        props.actions.doGetPopularCategoryProducts(category, '', false)
          .finally(() => this.markRequestCompleted(category.id, 'popular'));
      }
    });

    // Phase 2: Load remaining categories with delay to avoid overwhelming the browser
    const remainingCategories = props.categories.slice(6);
    remainingCategories.forEach((category, index) => {
      setTimeout(() => {
        if (!category.popular_products && !this.isRequestInFlight(category.id, 'popular')) {
          this.markRequestStarted(category.id, 'popular');
          props.actions.doGetPopularCategoryProducts(category, '', false)
            .finally(() => this.markRequestCompleted(category.id, 'popular'));
        }
      }, index * 100); // Stagger requests by 100ms
    });

    // Phase 3: Load full product lists only when needed (lazy loading)
    props.categories.forEach((category) => {
      if (category.popular_products && category.popular_products.length < 7 && !category.products) {
        // Only load if user might need to see "See All" - delay this
        setTimeout(() => {
          if (!this.isRequestInFlight(category.id, 'products')) {
            this.markRequestStarted(category.id, 'products');
            props.actions.doGetCategoryProducts(category, '', false)
              .finally(() => this.markRequestCompleted(category.id, 'products'));
          }
        }, 2000); // Wait 2 seconds before loading full product lists
      }
    });
  }

  _handleClickViewAll = (category) => {
    browserHistory.push('/supplies/catalog/' + category.id);
  };

  renderItem = (item) => {
    let products = mergePopularProducts(item.popular_products, item.products);

    if (this.props.inLimitedViewingMode) {
      products = filterInLimitedCatalog(products, this.props.inCatalogCache);
    }

    const isLoadingThisCategory = this.isRequestInFlight(item.id, 'popular') || this.isRequestInFlight(item.id, 'products');
    const hasAnyProducts = products && products.length > 0;
    const shouldShowCategory = hasAnyProducts || isLoadingThisCategory;

    // Always render the category structure to avoid layout shifts
    return (
      <div key={item.id} className="category-catalog-all-row">
        <div className="category-catalog-row-header">
          <h4>Top {item.name}</h4>
          {hasAnyProducts && products.length > SEE_ALL_CATEGORY_THRESHOLD && (
            <Link to={'/supplies/catalog/' + item.id}>See All</Link>
          )}
        </div>
        
        {/* Show loading state while this specific category is loading */}
        {isLoadingThisCategory && <ProductListRowLoading />}
        
        {/* Show products when available */}
        {hasAnyProducts && !isLoadingThisCategory && (
          <MemoizedProductListRow
            items={products}
            onClickViewAll={this._handleClickViewAll.bind(this, item)}
            maxDisplay={SEE_ALL_CATEGORY_THRESHOLD}
          />
        )}
        
        {/* Show placeholder if no products and not loading */}
        {!hasAnyProducts && !isLoadingThisCategory && (
          <div className="category-no-products">
            No products available in this category
          </div>
        )}
      </div>
    );
  };

  render() {
    return (
      <div className="catalog-all">
        {this.props.categories.map(this.renderItem)}
      </div>
    );
  }
}

OfficeluvFullCatalog.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      popular_products: PropTypes.array,
      location_popular_products: PropTypes.array,
    })
  ).isRequired,
  actions: PropTypes.shape({
    doGetPopularCategoryProducts: PropTypes.func.isRequired,
  }).isRequired,
  inLimitedViewingMode: PropTypes.bool.isRequired,
  isRequesting: PropTypes.bool.isRequired,
  inCatalogCache: PropTypes.array.isRequired,
};

function filterParents(c) {
  return !c.parent_id;
}

function filterEnabledCategories(locationParentCategories, category) {
  return locationParentCategories.includes(category.id);
}

function sortByOrder(a, b) {
  if (!a.order) return 1;
  if (a.order < b.order) return -1;
  if (a.order > b.order) return 1;
}

export function mapStateToProps(state) {
  const locationParentCategories = mapLocationParentCategoriesIds(
    state.locationParentProductCategories.items
  );

  return {
    categories: state.productCategories.items
      .filter((item) => filterEnabledCategories(locationParentCategories, item))
      .filter(filterParents)
      .sort(sortByOrder),
    inLimitedViewingMode: state.application.inLimitedViewingMode,
    isRequesting: !!state.productCategories.requesting.length,
    inCatalogCache: state.products.in_catalog,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        doGetPopularCategoryProducts,
        doGetCategoryProducts,
      },
      dispatch
    ),
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.productCategories.items === next.productCategories.items &&
    prev.locationParentProductCategories.items ===
      next.locationParentProductCategories.items &&
    prev.application.inLimitedViewingMode ===
      next.application.inLimitedViewingMode &&
    prev.productCategories.requesting === next.productCategories.requesting &&
    prev.products.in_catalog === next.products.in_catalog
  );
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual,
})(OfficeluvFullCatalog);
